import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import triggerPostToVerifyBetaAccess from '../functions/beta/triggerPostToVerifyBetaAccess';
import triggerPostToSaveBetaAccessRequest from '../functions/beta/triggerPostToSaveBetaAccessRequest';

const ExclusivityModal = ({ onUnlock }) => {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | error | success
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('idle');
    setError(null);

    try {
      const res = await triggerPostToVerifyBetaAccess(username);
      
      setTimeout(() => {
        if (res === true) {
          localStorage.setItem(
            'cowjuice_beta_request_username',
            username.trim().toLowerCase().replace(/^@/, '')
          );
          setStatus('success');
          setLoading(false);

          

          // Delay fade-out by 750ms so user sees the success styling
          setTimeout(() => {
            onUnlock(true); // Verified beta tester.
          }, 1500); // or 750 if you want it to match your animation timing
        } else {
          setAttempted(true);
          setStatus('error');
          setError(
            <p className="text-[10px] text-cowjuice-red font-mono uppercase">
              You don't have access yet. Try asking Cow Juice Man nicely.<sup>[2]</sup>
            </p>
          );
          setLoading(false);
        }
      }, 1000); // Visual delay before revealing success or failure
    } catch (err) {
      console.error('Client error verifying beta access:', err);
      setAttempted(true);
      setStatus('error');
      setError(
        <p className="text-[10px] text-cowjuice-red font-mono uppercase">
          Something went wrong.
        </p>
      );
      setLoading(false);
    }
  };


  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await triggerPostToSaveBetaAccessRequest(username);

      setTimeout(() => {
        if (result.success) {
          setStatus('success');
          setError('Access request sent! Returning to Retort Land...');
          setLoading(false);

          setTimeout(() => {
            navigate('/ultra-retorted-milk');
            onUnlock(false); // Not a verified beta tester, but requested access
          }, 2000);
        } else if (result.reason === 'duplicate') {
          setStatus('error');
          setError(
            <p className="text-[10px] text-cowjuice-red font-mono uppercase">
              It looks like you've already requested access. Please exhibit patience. Cow Juice Man is considering your request.<sup>[2]</sup>
            </p>
          );
          setLoading(false);
        } else {
          setStatus('error');
          setError(
            <p className="text-[10px] text-cowjuice-red font-mono uppercase">
              Something went wrong submitting your request. Try again later.
            </p>
          );
          setLoading(false);
        }
      }, 1000); // Force a 1000ms delay in all cases
    } catch (err) {
      console.error('Error submitting access request:', err);
      setTimeout(() => {
        setStatus('error');
        setError(
          <p className="text-[10px] text-cowjuice-red font-mono uppercase">
            Something went wrong. Try again later.
          </p>
        );
        setLoading(false);
      }, 1000);
    }
  };

  const reset = () => {
    setUsername('');
    setStatus('idle');
    setAttempted(false);
    setError(null);
  };

  // We run the reset cleanup anytime the username goes back to empty
  useEffect(() => {
    if (username === '') {
      reset();
    }
  }, [username]);

  const btnBase = `flex w-full items-center justify-start font-mono text-xs uppercase font-bold py-3 sm:py-4 px-4 rounded-xl border-[0.5px] transition-colors duration-300`;
  const btnIdle = `${username === '' ? 'bg-white text-black border-black' : 'bg-black text-white border-black'}`;
  const btnAttempted = 'bg-white text-black border-[0.5px] border-black hover:bg-cowjuice-gold/10 animate-pulse animate-delay-[250ms]';
  const btnError = 'bg-black text-white shake';
  const btnSuccess = 'bg-cowjuice-gold text-white';

  const getButtonClass = () => {
    if (status === 'success') return `${btnBase} ${btnSuccess}`;
    if (status === 'error' && !attempted) return `${btnBase} ${btnError}`;
    if (attempted) return `${btnBase} ${btnAttempted}`;
    return `${btnBase} ${btnIdle}`;
  };

  return (
    <div className={`fixed inset-0 z-[700] flex flex-col justify-end space-y-2 px-6 pt-8 pb-6 bg-gradient-to-t from-white via-white/50 to-white/5 backdrop-blur-sm animate-fade animate-delay-[000ms]`}>
      {/* Close Button */}
      <button
        onClick={() => {
          if (window.location.pathname === '/') {
          onUnlock(false);
        } else {
          navigate('/shopify/products');
        };
      }}
        className="absolute top-6 left-6 text-xs text-black font-mono opacity-60"
      >
        &lt;&lt;
      </button>
      <p className="absolute top-6 right-6 text-[10px] uppercase opacity-60 text-black font-mono">[Click arrows to go back]</p>

      <motion.div
        layout
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="flex flex-col items-center w-full max-w-xl mx-auto space-y-3"
      >
        <div className="flex flex-col items-center w-full max-w-xl mx-auto space-y-2">
          <p className="font-mono text-[10px] opacity-60 uppercase w-full text-left">Cow Juice • Private Beta Testing Program<sup class="text-[8px]"> [1]</sup></p>
          <input
            type="text"
            placeholder="Enter Email to Access Portal"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setStatus('idle');
            }}
            className="w-full border-[0.5px] border-black px-4 py-3 sm:py-4 rounded-xl bg-white focus:outline-none font-mono text-xs h-auto uppercase animate-flip-up"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {username !== '' && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                attempted ? handleRequestAccess(e) : handleSubmit(e);
              }}
              disabled={status === 'success'}
              className={getButtonClass()}
            >
              {loading ? (
                <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 animate-spin">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <span>
                  {(status === 'success' && !attempted)
                    ? 'Welcome!'
                    : attempted
                    ? (attempted && status === 'success') ? 'Access Request Sent! Returning to Retort Land' : <>Request Access<sup>[2]</sup></>
                    : 'Enter Beta Access Portal'}
                </span>
              )}
            </button>

            {attempted && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  reset();
                }}
                className="flex flex-row w-full items-start space-x-2 text-left opacity-60 hover:opacity-100 transition-opacity duration-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.0} stroke="currentColor" className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                <span className="ml-2 text-[10px] font-mono uppercase">Reset</span>
              </button>
            )}
          </>
        )}

        {status === 'error' && (
          <p className="flex w-full animate-flip-up leading-3">{error}</p>
        )}
        {(status === 'success' && !attempted) && (
          <p className="flex w-full text-cowjuice-gold text-[10px] font-mono uppercase animate-flip-up">✔ Access Granted</p>
        )}

        <p className="font-mono text-[10px] leading-3 uppercase opacity-60 animate-flip-down animate-delay-[800ms]">
          <sup>[1]</sup> A select few will be granted access to the Cow Juice Private Beta. See if you've been chosen.
          
        </p>
        <p className={`font-mono text-[10px] leading-3 uppercase animate-flip-down animate-delay-[800ms] ${attempted ? 'opacity-60' : 'opacity-0'} transition-opacity duration-300`}>
          <sup>[2]</sup> Strictly speaking, you can ask Cow Juice Man for access at any time. However, our lawyers have advised us to disclaim that you we are in no way obligated to grant you access, and that we reserve the right to deny access to anyone for any reason. This is a <span class="itali">private</span> beta, after all.
        </p>
      </motion.div>
    </div>
  );
};

export default ExclusivityModal;
